'use client';

/**
 * EmptyState — freundlicher Leerzustand mit Icon/Illustration statt leerer Fläche.
 * Ersetzt das „nackte Textzeile"-Muster (z. B. „noch keine Mahlzeit") durch
 * eine ruhige, markenkonforme Karte mit Icon-Badge, Titel, Caption und optionaler
 * Aktion. Theme-reaktiv (hell + dunkel).
 */

import React, { type ReactNode } from 'react';
import { useColors } from '../theme/ThemeProvider';
import { fontSize, fontWeight, radius, space } from '../theme/tokens';
import { Icon, type IconName } from './icons/Icon';

export interface EmptyStateProps {
  icon: IconName;
  title: string;
  caption?: string;
  /** Optionaler Call-to-Action (z. B. ein <Button>). */
  action?: ReactNode;
}

export function EmptyState({ icon, title, caption, action }: EmptyStateProps) {
  const colors = useColors();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: `${space[8]}px ${space[5]}px`,
        gap: space[3],
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: radius.full,
          background: colors.accentSoft,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.accentInk,
        }}
      >
        <Icon name={icon} size={26} />
      </div>
      <div
        style={{
          fontSize: fontSize.lg,
          fontWeight: fontWeight.bold,
          color: colors.text,
        }}
      >
        {title}
      </div>
      {caption && (
        <div
          style={{
            fontSize: fontSize.base,
            color: colors.textDim,
            maxWidth: 260,
            lineHeight: 1.5,
          }}
        >
          {caption}
        </div>
      )}
      {action && <div style={{ marginTop: space[2] }}>{action}</div>}
    </div>
  );
}
